import {
  SafeAreaView,
  ScrollView,
  StyleSheet,
  Text,
  View,
  ActivityIndicator,
  TouchableOpacity,
  KeyboardAvoidingView,
  Platform,
} from "react-native";
import React, { useEffect, useState } from "react";
import { Colors } from "@/constants/Colors";
import Header from "@/components/ui/Header";
import { Input } from "@/components/Input";
import Select from "@/components/Select";
import { US_STATES } from "@/constants/data";
import { UserProfile } from "@/types";
import CommonServices from "@/services/commonServices";
import { showError, showSuccess } from "@/utils/toast";
import { router } from "expo-router";
import { FontFamilies } from "@/constants/Typography";
import useUserStore from "@/store/user";

const ProfileScreen = () => {
  const { userProfile: user } = useUserStore();
  const [userProfile, setUserProfile] = useState<UserProfile>();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setIsLoading(true);
        const res = await CommonServices.getUserProfile();
        setUserProfile(res.data);
      } catch (error) {
        console.error("Error fetching user profile:", error);
        showError("Failed to load profile data", "Error");
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserProfile();
  }, []);

  const handleUpdateProfile = async () => {
    if (!userProfile) return;

    try {
      setIsSaving(true);
      // Exclude email from update payload as it cannot be updated
      const { email, ...updateData } = userProfile;
      await CommonServices.updateUserProfile(updateData as UserProfile);
      showSuccess("Profile updated successfully", "Success");
    } catch (error) {
      console.error("Error updating profile:", error);
      showError("Failed to update profile", "Error");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <SafeAreaView style={styles.container}>
        <Header title="Personal details" />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={Colors.primary} />
          <Text style={styles.loadingText}>Loading profile...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <Header
        title="Personal details"
        rightButton={
          <TouchableOpacity onPress={handleUpdateProfile} disabled={isSaving}>
            <Text style={styles.saveButtonText}>
              {isSaving ? "Saving..." : "Save"}
            </Text>
          </TouchableOpacity>
        }
      />

      <KeyboardAvoidingView
        style={styles.keyboardAvoidingView}
        behavior={Platform.OS === "ios" ? "padding" : undefined}
        keyboardVerticalOffset={Platform.OS === "ios" ? 80 : 0}
      >
        <ScrollView
          style={[styles.content, isSaving && styles.disabledContent]}
          showsVerticalScrollIndicator={false}
          scrollEnabled={!isSaving}
          keyboardShouldPersistTaps="handled"
        >
          {isSaving && (
            <View style={styles.savingOverlay}>
              <ActivityIndicator size="small" color={Colors.primary} />
              <Text style={styles.savingText}>Updating profile...</Text>
            </View>
          )}

          <View style={styles.row}>
            <Input
              placeholder="First name"
              label="First name"
              containerStyle={styles.inputGrow}
              style={styles.input}
              required
              value={userProfile?.firstName}
              onChangeText={(text) =>
                setUserProfile({
                  ...userProfile,
                  firstName: text,
                } as UserProfile)
              }
              editable={!isSaving}
            />
            <Input
              placeholder="Last name"
              label="Last name"
              containerStyle={styles.inputGrow}
              style={styles.input}
              required
              value={userProfile?.lastName}
              onChangeText={(text) =>
                setUserProfile({
                  ...userProfile,
                  lastName: text,
                } as UserProfile)
              }
              editable={!isSaving}
            />
          </View>
          <View style={styles.row}>
            <Input
              placeholder="Address"
              label="Address"
              containerStyle={styles.inputGrow}
              style={styles.input}
              required
              value={userProfile?.address}
              onChangeText={(text) =>
                setUserProfile({ ...userProfile, address: text } as UserProfile)
              }
              editable={!isSaving}
            />
            <Input
              placeholder="Apt/Suite"
              label="Apt/Suite"
              containerStyle={styles.inputGrow0}
              style={styles.input}
              required
              value={userProfile?.aptSuite}
              onChangeText={(text) =>
                setUserProfile({
                  ...userProfile,
                  aptSuite: text,
                } as UserProfile)
              }
              editable={!isSaving}
            />
          </View>
          <Input
            placeholder="City"
            label="City"
            containerStyle={styles.inputGrow}
            style={styles.input}
            required
            value={userProfile?.city}
            onChangeText={(text) =>
              setUserProfile({ ...userProfile, city: text } as UserProfile)
            }
            editable={!isSaving}
          />
          <View style={styles.row}>
            <Select
              options={US_STATES.map((state) => ({
                label: state,
                value: state,
              }))}
              onSelect={(value) =>
                setUserProfile({
                  ...userProfile,
                  state: value.value,
                } as UserProfile)
              }
              required
              placeholder="State"
              label="State"
              containerStyle={styles.inputGrow}
              style={styles.input}
              value={userProfile?.state}
              disabled={isSaving}
            />
            <Input
              placeholder="Zip code"
              label="Zip code"
              containerStyle={styles.inputGrow}
              style={styles.input}
              required
              value={userProfile?.zipCode}
              onChangeText={(text) =>
                setUserProfile({ ...userProfile, zipCode: text } as UserProfile)
              }
              editable={!isSaving}
            />
          </View>
          <Input
            placeholder="Email address"
            label="Email address"
            containerStyle={styles.inputGrow}
            style={styles.input}
            disabled
            value={user?.email}
          />
          <Input
            placeholder="Phone Number"
            label="Phone Number"
            containerStyle={styles.inputGrow}
            style={styles.input}
            required
            value={userProfile?.phoneNumber}
            onChangeText={(text) =>
              setUserProfile({
                ...userProfile,
                phoneNumber: text,
              } as UserProfile)
            }
            editable={!isSaving}
          />
          <Input
            placeholder="e.g., Account PIN: 1234, Customer ID: ABC123, Last 4 SSN: 5678, or special instructions for negotiation"
            label="Additional Details"
            containerStyle={styles.inputGrow}
            style={[styles.input, styles.textArea]}
            multiline
            numberOfLines={4}
            textAlignVertical="top"
            value={userProfile?.additionalDetails}
            onChangeText={(text) =>
              setUserProfile({
                ...userProfile,
                additionalDetails: text,
              } as UserProfile)
            }
            editable={!isSaving}
          />
          <TouchableOpacity
            style={styles.deleteAccountButton}
            onPress={() => router.push("/account/delete")}
          >
            <Text style={styles.deleteAccountButtonText}>
              Delete your account
            </Text>
          </TouchableOpacity>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

export default ProfileScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.background,
  },
  keyboardAvoidingView: {
    flex: 1,
  },
  content: {
    flex: 1,
    paddingHorizontal: 16,
    paddingTop: 16,
  },
  disabledContent: {
    opacity: 0.7,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  inputGrow: {
    flex: 1,
  },
  inputGrow0: {
    flexGrow: 0,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 16,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: Colors.text,
    textAlign: "center",
  },
  savingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    backgroundColor: Colors.background,
    paddingVertical: 12,
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border,
  },
  savingText: {
    marginLeft: 8,
    fontSize: 14,
    color: Colors.text,
  },
  deleteAccountButton: {
    padding: 16,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
  },
  deleteAccountButtonText: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#848484",
  },
  saveButtonText: {
    fontSize: 14,
    fontFamily: FontFamilies.InterTight.SemiBold,
    color: Colors.primary,
  },
  input: {
    backgroundColor: "#fff",
    borderColor: "#D8D9DB",
  },
  textArea: {
    height: 100,
    paddingTop: 12,
  },
});
